import { Box, Button, DialogContent, FormControl, Stack } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useState } from "react";

import ProductLocationsAutocompleteOrCreate from "@/common/components/Entity/ProductLocation/ProductLocationsAutocompleteOrCreate";
import AppModal, { AppModalProps } from "@/common/components/Modals/AppModal";
import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import { TextHelper } from "@/common/helpers/text";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { AssetSubscriptionPlanDto } from "@/core/api/generated/v1";

interface Props extends Omit<AppModalProps, "children"> {
  assetSubscriptionPlan: AssetSubscriptionPlanDto;
  onAdded: () => void;
}

export default function AssetSubscriptionPlanAddProductProductLocationsModal({
  assetSubscriptionPlan,
  onAdded,
  ...modalProps
}: Props) {
  const { enqueueSnackbar } = useAppSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProductLocationIds, setSelectedProductLocationIds] = useState<
    string[] | undefined
  >(undefined);

  const reset = () => {
    setSelectedProductLocationIds(undefined);
  };

  const handleAdd = async () => {
    setIsSubmitting(true);

    try {
      await apiClient.assetSubscriptionPlansApi.apiV1AssetSubscriptionPlansAssetSubscriptionPlanIdProductLocationsPost(
        {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          assetSubscriptionPlanId: assetSubscriptionPlan.id!,
          addAssetSubscriptionPlanProductLocationsDto: {
            productLocationIds: selectedProductLocationIds,
          },
        },
      );
      enqueueSnackbar(
        `Product ${TextHelper.pluralize(
          "location",
          selectedProductLocationIds?.length || 0,
        )} added to asset subscription plan`,
        {
          variant: "success",
        },
      );
      modalProps?.onClose && modalProps?.onClose({}, "escapeKeyDown");
      onAdded && onAdded();
      reset();
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppModal fullWidth keepMounted={false} {...modalProps}>
      <AppModalTitle
        title={"Add sales locations to asset subscription plan"}
        withClose
        onClose={() => modalProps?.onClose && modalProps?.onClose({}, "escapeKeyDown")}
      />
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <FormControl margin='dense' fullWidth>
              <ProductLocationsAutocompleteOrCreate
                autocompleteProps={{
                  fullWidth: true,
                  entityIds: selectedProductLocationIds,
                  entities: undefined,
                  searchFilters: {
                    notForAssetSubscriptionPlanId: assetSubscriptionPlan?.id || undefined,
                  },
                  onChange: (newValues) =>
                    setSelectedProductLocationIds(newValues?.map((x) => x.id!) || undefined),
                }}
                createUpdateProps={{
                  defaultValues: {},
                }}
                onCreate={(newValue) => {
                  setSelectedProductLocationIds([
                    ...(selectedProductLocationIds || []),
                    newValue.id!,
                  ]);
                }}
              />
            </FormControl>
          </Box>

          <Button
            fullWidth
            variant='contained'
            disabled={isEmpty(selectedProductLocationIds)}
            loading={isSubmitting}
            onClick={handleAdd}
          >
            Save
          </Button>
        </Stack>
      </DialogContent>
    </AppModal>
  );
}
